/**
 * FitnessOrdersTracking — متابعة الطلبات
 * Feed of all orders placed via the 606 Fitness store/app, showing
 * member, items, total, status and timestamp.
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FileText, Loader2, Search, RefreshCw, User, Calendar, Package, DollarSign, Plus, Trash2, Edit3 } from 'lucide-react';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import FitnessLogModal from '../components/FitnessLogModal';
import { useLanguage } from '../context/LanguageContext';
import { getAuthHeaders } from '../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  pending:    { ar: 'بانتظار المعالجة', en: 'Pending',   color: 'bg-amber-500/10 text-amber-400 border-amber-500/30' },
  processing: { ar: 'قيد التحضير',       en: 'Processing', color: 'bg-blue-500/10 text-blue-400 border-blue-500/30' },
  shipped:    { ar: 'تم الشحن',          en: 'Shipped',   color: 'bg-purple-500/10 text-purple-400 border-purple-500/30' },
  delivered:  { ar: 'تم التسليم',        en: 'Delivered', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' },
  cancelled:  { ar: 'ملغي',              en: 'Cancelled', color: 'bg-red-500/10 text-red-400 border-red-500/30' },
};

const ORDER_FIELDS = [
  { key: 'user_name',    labelAr: 'اسم العميل', labelEn: 'Customer Name', type: 'autocomplete', required: true, placeholderAr: 'اختر أو اكتب الاسم', placeholderEn: 'Pick or type name' },
  { key: 'order_number', labelAr: 'رقم الطلب', labelEn: 'Order Number',  type: 'text', placeholderAr: 'يُولّد تلقائياً إذا تركته فارغاً', placeholderEn: 'Auto-generated if blank' },
  { key: 'items_count',  labelAr: 'عدد المنتجات', labelEn: 'Items Count', type: 'number' },
  { key: 'total',        labelAr: 'المبلغ الإجمالي', labelEn: 'Total', type: 'number', step: '0.01' },
  { key: 'status',       labelAr: 'الحالة', labelEn: 'Status', type: 'select', default: 'pending', options: [
      { value: 'pending',    ar: 'بانتظار المعالجة', en: 'Pending' },
      { value: 'processing', ar: 'قيد التحضير',     en: 'Processing' },
      { value: 'shipped',    ar: 'تم الشحن',        en: 'Shipped' },
      { value: 'delivered',  ar: 'تم التسليم',      en: 'Delivered' },
      { value: 'cancelled',  ar: 'ملغي',            en: 'Cancelled' },
    ] },
  { key: 'notes',        labelAr: 'ملاحظات', labelEn: 'Notes', type: 'textarea', fullWidth: true },
];

export default function FitnessOrdersTracking() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/fitness/orders`, { headers: getAuthHeaders() });
      setOrders(res.data?.data || res.data?.orders || []);
    } catch (err) {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleSave = async (values) => {
    if (!values.user_name?.trim()) {
      toast.error(language === 'ar' ? 'اسم العميل مطلوب' : 'Customer name required');
      return;
    }
    setSaving(true);
    try {
      if (editTarget) {
        await axios.put(`${API_URL}/api/fitness/orders/${editTarget.id}`, values, { headers: getAuthHeaders() });
        toast.success(language === 'ar' ? 'تم تحديث الطلب' : 'Order updated');
      } else {
        await axios.post(`${API_URL}/api/fitness/orders`, values, { headers: getAuthHeaders() });
        toast.success(language === 'ar' ? 'تم إضافة الطلب' : 'Order added');
      }
      closeModal();
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل الحفظ' : 'Failed'));
    } finally {
      setSaving(false);
    }
  };

  const openAdd = () => { setEditTarget(null); setModalOpen(true); };
  const openEdit = (order) => { setEditTarget(order); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setEditTarget(null); };

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'ar' ? 'حذف هذا الطلب؟' : 'Delete this order?')) return;
    try {
      await axios.delete(`${API_URL}/api/fitness/orders/${id}`, { headers: getAuthHeaders() });
      toast.success(language === 'ar' ? 'تم الحذف' : 'Deleted');
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل');
    }
  };

  const visible = orders.filter((o) => {
    if (statusFilter !== 'all' && o.status !== statusFilter) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [o.order_number, o.user_name, o.id]
      .some((v) => v && String(v).toLowerCase().includes(q));
  });

  const counts = {
    all: orders.length,
    pending: orders.filter(o => o.status === 'pending').length,
    processing: orders.filter(o => o.status === 'processing').length,
    shipped: orders.filter(o => o.status === 'shipped').length,
    delivered: orders.filter(o => o.status === 'delivered').length,
    cancelled: orders.filter(o => o.status === 'cancelled').length,
  };

  return (
    <DashboardLayout
      title="متابعة الطلبات"
      titleEn="Order Tracking"
      subtitle="جميع طلبات المنتجات من تطبيق 606 Fitness"
      subtitleEn="All product orders placed via the 606 Fitness app"
      screenCode="FIT-004"
      backPath="/dashboard/606-fitness"
      onBack={() => navigate('/dashboard/606-fitness')}
      breadcrumb={[
        { labelAr: '606 FITNESS', labelEn: '606 FITNESS', path: '/dashboard/606-fitness' },
        { labelAr: 'متابعة الطلبات', labelEn: 'Order Tracking' },
      ]}
    >
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="fitness-orders-page">
        {/* Search + Refresh */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#71717A]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={language === 'ar' ? 'بحث برقم الطلب أو اسم العميل...' : 'Search by order number or member...'}
              className="w-full bg-white/[0.02] border border-white/10 rounded-xl py-3 px-10 text-white placeholder-[#71717A] focus:outline-none focus:border-emerald-500/50"
              data-testid="orders-search"
            />
          </div>
          <button
            onClick={fetchOrders}
            className="px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-emerald-400 hover:bg-emerald-500/20 transition-colors"
            data-testid="orders-refresh"
          >
            <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={openAdd}
            className="px-5 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-medium flex items-center gap-2 transition-colors"
            data-testid="orders-add-btn"
          >
            <Plus className="h-5 w-5" />
            {language === 'ar' ? 'إضافة طلب' : 'Add Order'}
          </button>
        </div>

        {/* Status filter chips */}
        <div className="flex flex-wrap gap-2 mb-6">
          {[
            { value: 'all',        labelAr: 'الكل',           labelEn: 'All' },
            { value: 'pending',    labelAr: 'بانتظار المعالجة', labelEn: 'Pending' },
            { value: 'processing', labelAr: 'قيد التحضير',     labelEn: 'Processing' },
            { value: 'shipped',    labelAr: 'تم الشحن',        labelEn: 'Shipped' },
            { value: 'delivered',  labelAr: 'تم التسليم',      labelEn: 'Delivered' },
            { value: 'cancelled',  labelAr: 'ملغي',            labelEn: 'Cancelled' },
          ].map((chip) => (
            <button
              key={chip.value}
              onClick={() => setStatusFilter(chip.value)}
              className={`px-4 py-2 rounded-xl border text-sm transition-all ${
                statusFilter === chip.value
                  ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400'
                  : 'bg-white/[0.02] border-white/10 text-[#A1A1AA] hover:border-white/20'
              }`}
              data-testid={`orders-filter-${chip.value}`}
            >
              {language === 'ar' ? chip.labelAr : chip.labelEn}
              <span className="mx-2 text-xs text-[#71717A]">({counts[chip.value] || 0})</span>
            </button>
          ))}
        </div>

        {/* Feed */}
        <div className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="text-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-400 mx-auto" />
            </div>
          ) : visible.length === 0 ? (
            <div className="text-center py-16 text-[#A1A1AA]">
              <FileText className="h-14 w-14 mx-auto mb-3 opacity-30" />
              <p>{language === 'ar' ? 'لا توجد طلبات' : 'No orders'}</p>
              <p className="text-xs text-[#606060] mt-2">
                {language === 'ar' ? 'ستظهر هنا كل طلبات المنتجات الجديدة من تطبيق 606 Fitness' : 'New product orders from the 606 Fitness app will appear here'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {visible.map((o) => {
                const st = STATUS_LABELS[o.status] || STATUS_LABELS.pending;
                return (
                  <div key={o.id || o.order_number || `${o.user_id}-${o.created_at}`} className="p-4 hover:bg-white/[0.02]" data-testid={`orders-row-${o.id || o.order_number}`}>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 bg-emerald-500/10 border border-emerald-500/30 rounded-xl flex items-center justify-center shrink-0">
                          <Package className="h-5 w-5 text-emerald-400" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-white font-medium truncate">
                            {o.order_number ? `#${o.order_number}` : (language === 'ar' ? 'طلب' : 'Order')}
                            {o.items_count ? <span className="text-xs text-[#A1A1AA] mx-2">({o.items_count} {language === 'ar' ? 'منتج' : 'items'})</span> : null}
                          </p>
                          <div className="text-xs text-[#A1A1AA] flex items-center gap-3 mt-1 flex-wrap">
                            <span className="flex items-center gap-1"><User className="h-3 w-3" />{o.user_name || '—'}</span>
                            {o.created_at && (
                              <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{new Date(o.created_at).toLocaleString()}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {o.total != null && (
                          <span className="text-sm font-semibold text-white flex items-center gap-1">
                            <DollarSign className="h-4 w-4 text-emerald-400" />
                            {Number(o.total).toFixed(2)} <span className="text-xs text-[#A1A1AA]">AED</span>
                          </span>
                        )}
                        <span className={`text-xs px-3 py-1 rounded-lg border ${st.color}`}>
                          {language === 'ar' ? st.ar : st.en}
                        </span>
                        {o.id && (
                          <>
                            <button
                              onClick={() => openEdit(o)}
                              className="p-1.5 rounded-lg bg-white/5 border border-white/10 text-[#A1A1AA] hover:text-blue-400 hover:border-blue-500/30 transition-colors"
                              data-testid={`orders-edit-${o.id}`}
                              title={language === 'ar' ? 'تعديل' : 'Edit'}
                            >
                              <Edit3 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(o.id)}
                              className="p-1.5 rounded-lg bg-white/5 border border-white/10 text-[#A1A1AA] hover:text-red-400 hover:border-red-500/30 transition-colors"
                              data-testid={`orders-delete-${o.id}`}
                              title={language === 'ar' ? 'حذف' : 'Delete'}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
      <FitnessLogModal
        open={modalOpen}
        onClose={closeModal}
        onSubmit={handleSave}
        title="إضافة طلب جديد"
        titleEn="Add New Order"
        fields={ORDER_FIELDS}
        saving={saving}
        color="blue"
        initialValues={editTarget}
      />
    </DashboardLayout>
  );
}
